import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Home, Users, ChartNoAxesCombined, TrendingUp, Bot, Menu, X, Calendar, Printer, FileText, Edit, User, GraduationCap, BarChart3, Calculator, ClipboardList, BookUser, ListPlus, FileScan, BookMarked } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SHOW_V1_PROBLEM_MENUS } from '../utils/menuVisibility';
import { V2_ROLES, OCR_ROLES } from '../utils/roles';
import './MobileBottomNav.css';

function MobileBottomNav({ portalMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userType } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [safeAreaBottom, setSafeAreaBottom] = useState(0);
  const handleMenuToggle = useCallback(() => setIsMenuOpen((open) => !open), []);
  const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);

  // 메뉴 밖에서 뒤로 가기·앞으로 가기를 해도 열린 메뉴가 새 페이지를 가리지 않게 한다.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.key]);

  // 열린 동안에만 스크롤을 잠근다. 닫기·페이지 이동·화면 전환 때 이전 스타일을 복원한다.
  // 상태 변경 함수 안에서 DOM을 바꾸지 않아 StrictMode의 반복 호출에도 같은 결과를 낸다.
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const targets = [
      [document.documentElement, { overflow: 'hidden', height: '100%' }],
      [document.body, {
        overflow: 'hidden', position: 'fixed', top: `-${scrollY}px`,
        left: '0', right: '0', width: '100%', touchAction: 'none',
        webkitUserSelect: 'none', userSelect: 'none',
      }],
    ];
    const previous = targets.map(([element, styles]) => Object.fromEntries(
      Object.keys(styles).map((key) => [key, element.style[key] || ''])
    ));
    targets.forEach(([element, styles]) => Object.assign(element.style, styles));

    return () => {
      targets.forEach(([element], index) => Object.assign(element.style, previous[index]));
      window.scrollTo(0, scrollY);
    };
  }, [isMenuOpen]);

  // Safe Area 감지 및 업데이트 - 동적 처리 개선
  useEffect(() => {
    let timeoutId = null;
    let rafId = null;
    
    const updateSafeArea = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          // 실제 CSS env() 값을 직접 읽기
          const testEl = document.createElement('div');
          testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
          document.body.appendChild(testEl);
          const computedStyle = window.getComputedStyle(testEl);
          const actualSafeArea = parseInt(computedStyle.paddingBottom) || 0;
          document.body.removeChild(testEl);
          
          // iOS Safari의 동적 Safe Area 처리
          // 주소창이 보이는 경우: safe area가 0이거나 매우 작음
          // 전체화면 모드: safe area가 실제 값을 가짐
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          
          if (isIOS && isSafari) {
            // 실제 safe area 값이 15px 미만이면 0으로 처리 (주소창 보이는 상태)
            const dynamicSafeArea = actualSafeArea < 15 ? 0 : actualSafeArea;
            setSafeAreaBottom(dynamicSafeArea);
          } else {
            // 다른 브라우저는 실제 값 사용
            setSafeAreaBottom(actualSafeArea);
          }
        });
      }, 100); // 100ms 디바운스
    };

    // 초기 실행
    updateSafeArea();
    
    // 이벤트 리스너
    window.addEventListener('resize', updateSafeArea);
    window.addEventListener('orientationchange', updateSafeArea);
    window.addEventListener('scroll', updateSafeArea); // iOS Safari 주소창 토글 감지
    
    // visualViewport API 사용 (지원하는 경우)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateSafeArea);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('resize', updateSafeArea);
      window.removeEventListener('orientationchange', updateSafeArea);
      window.removeEventListener('scroll', updateSafeArea);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateSafeArea);
      }
    };
  }, []);

  // 각 열의 마지막 항목이 하단 메뉴다. 예전 메뉴 순환·저장 인덱스를 쓰지 않고 네 항목을 고정한다.
  const columnMenus = useMemo(() => {
    const canUseV2 = V2_ROLES.includes(userType);
    const item = (icon, label, path, prefixes = [path]) => ({
      icon, label, path,
      isActive: prefixes.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)),
    });

    return [
      [
        item(GraduationCap, '시험분석', '/school-exams/analysis/1', ['/school-exams']),
        item(Calendar, '시험지제작', '/preset'),
        canUseV2 ? item(ClipboardList, '시험지', '/exam-papers') : null,
      ],
      [
        item(Users, '학생관리', '/student/1', ['/student', '/test-papers/student', '/test-result', '/result']),
        item(TrendingUp, '학습분석', '/learning-status'),
        canUseV2 ? item(BookUser, '학습', '/students-hub') : null,
      ],
      [
        userType !== 'teacher' ? item(BarChart3, '통계', '/statistics') : null,
        item(ChartNoAxesCombined, '폼레이팅', '/data'),
        item(Home, '대시보드', '/dashboard'),
      ],
      [
        userType === 'manager' ? item(Calculator, '매스컨셉', '/mathconcept') : null,
        OCR_ROLES.includes(userType) ? item(FileScan, 'OCR', '/ocr-hub') : null,
        canUseV2 ? item(ListPlus, '문제', '/problem-hub') : null,
      ],
      [
        canUseV2 ? item(BookMarked, '강의', '/syllabi') : null,
        item(FileText, '학습구성', '/testset', ['/testset', '/test-register']),
        userType !== 'teacher' ? item(User, '사용자관리', '/user-management', ['/user-management', '/teachers', '/managers']) : null,
      ],
    ];
  }, [location.pathname, userType]);

  // 시험지 · 학습 · 대시보드 · 문제 순서를 유지한다. 다섯 번째 버튼은 전체 메뉴를 연다.
  const mainMenuItems = columnMenus.slice(0, 4).map((column) => column[2]).filter(Boolean);

  const allMenuItems = useMemo(() => {
    // 5열을 유지해 OCR이 문제 바로 위에 놓이게 한다. 권한이 없는 칸은 비워 둔다.
    const rows = [0, 1, 2].flatMap((row) => columnMenus.map((column) => column[row]));
    if (!SHOW_V1_PROBLEM_MENUS) return rows;

    // v1 표시 설정을 다시 켜면 추가 행으로 복원한다. 하단의 고정 메뉴는 바뀌지 않는다.
    const legacyItem = (icon, label, path) => ({ icon, label, path, isActive: location.pathname.startsWith(path) });
    return [
      legacyItem(Printer, '수동출력', '/problem-print'),
      null,
      null,
      legacyItem(Bot, '문제생성', '/problem-agent'),
      legacyItem(Edit, '문제관리', '/problem-edit'),
      ...rows,
    ];
  }, [columnMenus, location.pathname]);

  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleNavigation = useCallback((path) => {
    const targetPath = path === '/problem-agent' && !isMobileDevice ? '/problem-agent/page/1' : path;
    if (location.pathname !== targetPath) navigate(targetPath);
    handleMenuClose();
  }, [navigate, isMobileDevice, location.pathname, handleMenuClose]);

  // 네비게이션바 컴포넌트
  const navigationBar = (
    <nav aria-label="모바일 내비게이션" className={`mobile-bottom-nav ${isMenuOpen ? 'mobile-bottom-nav--menu-open' : 'mobile-bottom-nav--glass'}`}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        width: '100%',
        borderRadius: '16px 16px 0 0',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingBottom: `${8 + safeAreaBottom}px`,
        height: `${68 + safeAreaBottom}px`,
        minHeight: `${68 + safeAreaBottom}px`,
      }}
    >
      {mainMenuItems.map((item) => (
        <button
          key={item.path}
          onClick={() => handleNavigation(item.path)}
          aria-current={item.isActive ? 'page' : undefined}
          className="nav-item"
          style={{ padding: 0 }}
        >
          <div className={`flex aspect-square size-[30px] min-h-[30px] min-w-[30px] items-center justify-center ${
            item.isActive 
              ? 'rounded-[8px] bg-primary' 
              : ''
          }`} style={{ margin: 0, padding: 0 }}>
            <item.icon 
              size={20} 
              className={`${item.isActive ? 'text-white' : ''}`}
              style={{ flexShrink: 0, margin: 0, display: 'block' }}
            />
          </div>
          <span className={`text-xs ${
            item.isActive 
              ? 'font-bold text-headerBg' 
              : ''
          }`}>{item.label}</span>
        </button>
      ))}
      <button
        onClick={handleMenuToggle}
        aria-expanded={isMenuOpen}
        aria-controls="mobile-navigation-menu"
        className="nav-item"
        style={{ padding: 0 }}
      >
        <div className={`flex aspect-square size-[30px] min-h-[30px] min-w-[30px] items-center justify-center ${
          isMenuOpen 
            ? 'rounded-[8px] bg-primary' 
            : ''
        }`} style={{ margin: 0, padding: 0 }}>
          <Menu 
            size={20} 
            className={`${isMenuOpen ? 'text-white' : ''}`}
            style={{ flexShrink: 0, margin: 0, display: 'block' }}
          />
        </div>
        <span className={`text-xs ${
          isMenuOpen 
            ? 'font-bold text-headerBg' 
            : ''
        }`}>메뉴</span>
      </button>
    </nav>
  );

  return (
    <>
      {/* 네비게이션바 - portalMode에 따라 렌더링 방식 결정 */}
      {portalMode ? createPortal(navigationBar, document.body) : navigationBar}

      {/* 전체 메뉴 오버레이 - 항상 createPortal로 document.body에 직접 렌더링 */}
      {isMenuOpen && createPortal(
        <div className="menu-overlay" onClick={handleMenuClose}>
          <div id="mobile-navigation-menu" className="menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <h3>전체 메뉴</h3>
              <button onClick={handleMenuClose} className="close-btn">
                <X size={24} />
              </button>
            </div>
            <div className="menu-grid" style={{ '--menu-column-count': columnMenus.length }}>
              {allMenuItems.map((item, index) => (
                item?.icon ? (
                  <button
                    key={index}
                    onClick={() => handleNavigation(item.path)}
                    className={`menu-grid-item ${item.isActive ? 'active' : ''}`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                ) : (
                  <div key={index} className="menu-grid-empty"></div>
                )
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default React.memo(MobileBottomNav); 